import React, { useState } from "react";
import { useRateChatSession } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { cn, sanitizeText } from "@/lib/utils";

export function ParticipantRatingWidget({
  assessmentToken,
  alreadyRated,
}: {
  assessmentToken: string;
  alreadyRated: boolean;
}) {
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { mutate: submitRating, isPending } = useRateChatSession({
    mutation: {
      onSuccess: () => setSubmitted(true),
    },
  });

  if (submitted || alreadyRated) {
    return (
      <div className="mt-5 bg-muted/60 rounded-xl px-5 py-4 text-center animate-in fade-in duration-300">
        <p className="text-sm font-medium text-foreground">Obrigado pelo feedback!</p>
        <p className="text-xs text-muted-foreground mt-1">Sua avaliação foi registrada.</p>
      </div>
    );
  }

  return (
    <div className="mt-5 bg-muted/60 rounded-xl px-5 py-4 text-left">
      <p className="text-xs font-semibold text-foreground mb-3 uppercase tracking-wide">Como foi sua experiência?</p>
      <div className="flex items-center gap-1 mb-3" role="group" aria-label="Avaliação de 1 a 5 estrelas">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            aria-label={`${star} estrela${star > 1 ? "s" : ""}`}
            className="p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => setSelected(star)}
          >
            <Star
              className={cn(
                "h-7 w-7 transition-colors",
                star <= (hovered || selected)
                  ? "fill-amber-400 text-amber-400"
                  : "text-muted-foreground",
              )}
            />
          </button>
        ))}
      </div>
      {selected > 0 && (
        <div className="space-y-3 animate-in fade-in duration-200">
          <Textarea
            placeholder="Algo que queira compartilhar? (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            rows={3}
            className="text-sm resize-none"
          />
          <Button
            size="sm"
            className="w-full"
            disabled={isPending}
            onClick={() => {
              const sanitizedComment = sanitizeText(comment.trim(), 1000);
              submitRating({
                assessmentToken,
                data: { rating: selected, ...(sanitizedComment ? { comment: sanitizedComment } : {}) },
              });
            }}
          >
            {isPending ? "Enviando…" : "Enviar avaliação"}
          </Button>
        </div>
      )}
    </div>
  );
}
